// Error types and helpers shared by the callback-style login flows (the magic-link link callback
// and the code-entry confirmation page). Kept in their own module so those flows can import them
// without pulling in — or being mocked alongside — the session-writing logic in authSession.ts.

// Thrown when a user authenticates successfully but has no permission to use this dashboard.
// Distinct from an authentication failure: requesting a fresh link/code will never fix it, so
// callers must NOT bounce the user back into the request-a-link loop.
export class DashboardAccessError extends Error {
  constructor(message = 'Your account does not have access to the Trove Dashboard.') {
    super(message);
    this.name = 'DashboardAccessError';
  }
}

// A backend AuthError surfaces through `exec` as an HttpError whose flat body carries
// `name: 'AuthError'` (see reware-api's errorFormatters). Those messages are written to be safe to
// show a user — they never reveal whether an email exists. Checked structurally so this module
// needn't depend on react-admin's HttpError class (which is mocked out in component tests).
const isBackendAuthError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { body?: { name?: string } }).body?.name === 'AuthError';

// The message to show a user for a failed sign-in. Only surfaces messages known to be user-safe
// (our own DashboardAccessError, and backend AuthError messages); everything else is
// developer-facing (a 500, a missing-token assertion), so fall back to a generic string.
export const getSignInErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof DashboardAccessError) {
    return error.message;
  }
  if (isBackendAuthError(error)) {
    return (error as { message?: string }).message || fallback;
  }
  return fallback;
};
